import { useState } from "react";
import { Sparkles, Plus, Trash2, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { SectionCard } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const INPUT_TYPES = ["text", "number", "select", "boolean"];
const blankAttr = () => ({ key: "", label: "", input_type: "text", required: false, options: [], unit: "", variant_eligible: false, filter: false });

export function CategoryAttributesEditor({ cats }) {
  const [catId, setCatId] = useState("");
  const [rows, setRows] = useState([]);
  const [inherited, setInherited] = useState([]);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const load = async (id) => {
    setCatId(id); setRows([]); setInherited([]);
    if (!id) return;
    try {
      const r = await api.get(`/marketplace/admin/categories/${id}/attributes-config`);
      setRows((r.data.own || []).map((a) => ({ ...blankAttr(), ...a, options: a.options || [] })));
      setInherited(r.data.inherited || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, blankAttr()]);
  const delRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const aiSuggest = async () => {
    if (!catId) return;
    setAiBusy(true);
    try {
      const r = await api.post(`/marketplace/admin/categories/${catId}/ai-suggest-attributes`);
      const sug = r.data.attributes || [];
      if (!sug.length) { toast.message("No AI suggestions", { description: r.data.note || "Add attributes manually." }); return; }
      // append only keys not already present
      setRows((rs) => {
        const have = new Set(rs.map((x) => x.key));
        const add = sug.filter((s) => s.key && !have.has(s.key)).map((s) => ({ ...blankAttr(), ...s, options: s.options || [] }));
        return [...rs, ...add];
      });
      toast.success(`AI suggested ${sug.length} attribute(s) — review and save`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const save = async () => {
    if (!catId) { toast.error("Select a category first"); return; }
    const attributes = [];
    for (const r of rows) {
      const key = (r.key || "").trim();
      if (!key) { toast.error("Every attribute needs a key"); return; }
      attributes.push({
        key, label: (r.label || key).trim(), input_type: r.input_type,
        required: !!r.required,
        options: r.input_type === "select" ? (Array.isArray(r.options) ? r.options : String(r.options).split(",").map((x) => x.trim()).filter(Boolean)) : [],
        unit: (r.unit || "").trim() || null, variant_eligible: !!r.variant_eligible, filter: !!r.filter,
      });
    }
    setBusy(true);
    try { await api.put(`/marketplace/admin/categories/${catId}/attributes`, { attributes }); toast.success("Attributes saved"); load(catId); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const optionsText = (r) => (Array.isArray(r.options) ? r.options.join(", ") : (r.options || ""));

  return (
    <SectionCard title="Category Attributes & Variants" icon={Sparkles} testid="mkt-cat-attrs">
      <p className="mb-3 text-xs text-slate-400">Define the fields sellers fill for a category (drives the dynamic product form, customer specs, filters and variant options). Child categories inherit parent attributes.</p>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select value={catId} onChange={(e) => load(e.target.value)} data-testid="catattr-select" className="min-w-[220px] rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600">
          <option value="">Select a category…</option>
          {cats.map((c) => <option key={c.category_id} value={c.category_id}>{c.parent_id ? "— " : ""}{c.name}</option>)}
        </select>
        {catId && <button onClick={aiSuggest} disabled={aiBusy} data-testid="catattr-ai" className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-700 disabled:opacity-60"><Sparkles className="h-3.5 w-3.5" /> {aiBusy ? "Thinking…" : "AI suggest attributes"}</button>}
      </div>

      {catId && inherited.length > 0 && (
        <div className="mb-3 rounded-xl bg-slate-50 p-2.5" data-testid="catattr-inherited">
          <p className="mb-1 text-[11px] font-semibold text-slate-500">Inherited from parent (read-only)</p>
          <div className="flex flex-wrap gap-1.5">{inherited.map((a) => <span key={a.key} className="rounded-full bg-white px-2 py-0.5 text-[11px] text-slate-500 ring-1 ring-slate-200">{a.label || a.key}{a.required ? " *" : ""}</span>)}</div>
        </div>
      )}

      {catId && (
        <div className="space-y-2" data-testid="catattr-rows">
          {rows.length === 0 && <p className="py-3 text-center text-sm text-slate-400">No attributes yet. Add one or use AI suggest.</p>}
          {rows.map((r, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-2.5" data-testid={`catattr-row-${i}`}>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <label className="text-[11px] text-slate-500">Key (snake_case)
                  <Input value={r.key} onChange={(e) => setRow(i, { key: e.target.value })} placeholder="e.g. size" data-testid={`catattr-key-${i}`} className="mt-0.5 h-8 text-xs" /></label>
                <label className="text-[11px] text-slate-500">Label
                  <Input value={r.label} onChange={(e) => setRow(i, { label: e.target.value })} placeholder="e.g. Size" data-testid={`catattr-label-${i}`} className="mt-0.5 h-8 text-xs" /></label>
                <label className="text-[11px] text-slate-500">Input type
                  <select value={r.input_type} onChange={(e) => setRow(i, { input_type: e.target.value })} data-testid={`catattr-type-${i}`} className="mt-0.5 h-8 w-full rounded-lg border border-slate-200 px-2 text-xs text-slate-600">
                    {INPUT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select></label>
                <label className="text-[11px] text-slate-500">Unit (optional)
                  <Input value={r.unit || ""} onChange={(e) => setRow(i, { unit: e.target.value })} placeholder="e.g. GB, kg" data-testid={`catattr-unit-${i}`} className="mt-0.5 h-8 text-xs" /></label>
              </div>
              {r.input_type === "select" && (
                <label className="mt-2 block text-[11px] text-slate-500">Options (comma-separated)
                  <Input value={optionsText(r)} onChange={(e) => setRow(i, { options: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="e.g. S, M, L, XL" data-testid={`catattr-options-${i}`} className="mt-0.5 h-8 text-xs" /></label>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-1 text-[11px] font-medium text-slate-600"><input type="checkbox" checked={!!r.required} onChange={(e) => setRow(i, { required: e.target.checked })} data-testid={`catattr-required-${i}`} /> Required</label>
                <label className="flex items-center gap-1 text-[11px] font-medium text-slate-600"><input type="checkbox" checked={!!r.variant_eligible} onChange={(e) => setRow(i, { variant_eligible: e.target.checked })} data-testid={`catattr-variant-${i}`} /> Variant option</label>
                <label className="flex items-center gap-1 text-[11px] font-medium text-slate-600"><input type="checkbox" checked={!!r.filter} onChange={(e) => setRow(i, { filter: e.target.checked })} data-testid={`catattr-filter-${i}`} /> Buyer filter</label>
                <button onClick={() => delRow(i)} className="ml-auto rounded-lg p-1.5 text-slate-300 hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2 pt-1">
            <button onClick={addRow} data-testid="catattr-add" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Plus className="h-3.5 w-3.5" /> Add attribute</button>
            <button onClick={save} disabled={busy} data-testid="catattr-save" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-60"><Save className="h-3.5 w-3.5" /> {busy ? "Saving…" : "Save attributes"}</button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}
